import { useEffect } from "react";
import { useRecoilValue } from "recoil"
import authAtom from "../atoms/auth/auth.atom"
import { Permission } from "../components/modals/view-modals/view-permission-modal";
import useGetAction from "../actions/get-actions";

export type AppPermissions = {
  canActivateDeactivateAdmin: boolean;
  canActivateDeactivateCompany: boolean;
  canActivateDeactivateEstate: boolean;
  canActivateDeactivateUnit: boolean;
  canApproveRejectUserReservation: boolean;
  canArchiveUnarchiveBillingItem: boolean;
  canArchiveUnarchiveBillingTemplate: boolean;
  canArchiveUnarchiveUser: boolean;
  canChangePassword: boolean;
  canCreateAccountSetup: boolean;
  canCreateAdmin: boolean;
  canCreateBillingItem: boolean;
  canCreateBillingTemplate: boolean;
  canCreateChartOfAccount: boolean;
  canCreateCompany: boolean;
  canCreateComplaint: boolean;
  canCreateEstate: boolean;
  canCreatePaymentGateway: boolean;
  canCreateSecurity: boolean;
  canCreateUnit: boolean;
  canCreateUser: boolean;
  canDeleteBillingItem: boolean;
  canDeleteComplaint: boolean;
  canDeleteEstate: boolean;
  canDeleteInvoiceTemplate: boolean;
  canEvictUser: boolean;
  canFundWallet: boolean;
  canGenerateCommonAreaToken: boolean;
  canGenerateEmergencyToken: boolean;
  canGenerateInvoice: boolean;
  canGenerateManualInvoice: boolean;
  canGeneratePropertyUnitToken: boolean;
  canGenerateTamperCode: boolean;
  canGetBatches: boolean;
  canGrantModuleAccess: boolean;
  canListAccountSetups: boolean;
  canListAdmins: boolean;
  canListAllTransactionsReport: boolean;
  canListBillingItems: boolean;
  canListBillingTemplates: boolean;
  canListChartOfAccounts: boolean;
  canListCommonAreaTokens: boolean;
  canListCompanies: boolean;
  canListComplaints: boolean;
  canListEmergencyTokens: boolean;
  canListEstates: boolean;
  canListInvoiceReport: boolean;
  canListInvoiceTemplates: boolean;
  canListInvoiceTransactionsReport: boolean;
  canListInvoices: boolean;
  canListMeters: boolean;
  canListModules: boolean;
  canListPaymentGateways: boolean;
  canListPermissions: boolean;
  canListPriceSettings: boolean;
  canListPropertyUnitTokens: boolean;
  canListRoles: boolean;
  canListSecurities: boolean;
  canListTamperCodes: boolean;
  canListUnits: boolean;
  canListUsers: boolean;
  canListVendingTransactionsReport: boolean;
  canListVisitations: boolean;
  canListWalletBalanceSummariesReport: boolean;
  canListWallets: boolean;
  canNotifyUnits: boolean;
  canProcessBatch: boolean;
  canResendCreatePasswordEmail: boolean;
  canSuspendUnitVend: boolean;
  canUpdateAccountSetup: boolean;
  canUpdateAdmin: boolean;
  canUpdateBillingItem: boolean;
  canUpdateBillingTemplate: boolean;
  canUpdateChartOfAccount: boolean;
  canUpdateCompany: boolean;
  canUpdateComplaintStatus: boolean;
  canUpdateEstate: boolean;
  canUpdateInvoiceTemplate: boolean;
  canUpdateMeter: boolean;
  canUpdatePaymentGateway: boolean;
  canUpdatePriceSettings: boolean;
  canUpdateProfile: boolean;
  canUpdateRole: boolean;
  canUpdateSecurity: boolean;
  canUpdateUnit: boolean;
  canUpdateUser: boolean;
  canViewAccountSetup: boolean;
  canViewAdmin: boolean;
  canViewBillingItem: boolean;
  canViewBillingTemplate: boolean;
  canViewChartOfAccounts: boolean;
  canViewCommonAreaToken: boolean;
  canViewCompany: boolean;
  canViewComplaint: boolean;
  canViewComplaintAnalytics: boolean;
  canViewEmergencyToken: boolean;
  canViewEstate: boolean;
  canViewEstateAnalytics: boolean;
  canViewHomeAnalytics: boolean;
  canViewInvoice: boolean;
  canViewInvoiceTemplate: boolean;
  canViewMeter: boolean;
  canViewPaymentGateway: boolean;
  canViewPriceSettings: boolean;
  canViewProfile: boolean;
  canViewPropertyUnitToken: boolean;
  canViewRole: boolean;
  canViewSecurity: boolean;
  canViewSecurityAnalytics: boolean;
  canViewTamperCode: boolean;
  canViewUnit: boolean;
  canViewUnitAnalytics: boolean;
  canViewUser: boolean;
  canViewVisitation: boolean;
  canViewVisitationAnalytics: boolean;
  canViewWallet: boolean;
};

const parsePermissions = (
  permissions: { slug: string; value: number | boolean }[]
): AppPermissions => {
  return permissions.reduce((acc, permission) => {
    const camelCase = permission.slug
      .split('-')
      .map((word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join('');

    const key = `can${camelCase.charAt(0).toUpperCase() + camelCase.slice(1)}`;
    acc[key as keyof AppPermissions] = permission.value === 1 || permission.value === true;
    return acc;
  }, {} as AppPermissions);
};

// Safe default — all permissions off until resolved
export const defaultPermissions: AppPermissions = new Proxy({} as AppPermissions, {
  get: () => false
});

export const useAdminPermissions = () => {
  const { user } = useRecoilValue(authAtom);
  const rawPermissions = user?.role?.permissions;
  const roleId = user?.role?.id;

  const {
    data: permissionData,
    isLoading: loadingPermissionData,
    isError: isPermissionError,
    refetch: refetchPermissions,
  } = useGetAction<{
    data: {
      name: string;
      slug: string;
      permissions: Permission[];
    };
  }>({
    url: `/admin/settings/roles/${roleId}`,
    // Use a stable, role-specific cache key so different roles don't share data
    funcKey: `admin-settings-roles-${roleId}`,
    // Only fetch when a valid roleId is present
    enabled: !!roleId,
  });

  // Refresh permissions every 10 minutes
  useEffect(() => {
    if (!roleId) return;
    const interval = setInterval(() => {
      refetchPermissions();
    }, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(interval);
  }, [roleId, refetchPermissions]);

  const permissions: Permission[] = permissionData?.data.permissions ?? [];

  let activePermissions;

  if (isPermissionError) {
    // On error, fallback to cached rawPermissions from Recoil
    activePermissions = rawPermissions;
  } else if (permissions.length > 0) {
    // Use real-time permissions if successfully loaded
    activePermissions = permissions;
  } else if (loadingPermissionData) {
    // While loading initially, default to all false as requested
    return defaultPermissions;
  } else {
    // Fallback if not loading, no error, and no permissions returned
    activePermissions = rawPermissions;
  }

  if (!activePermissions || !Array.isArray(activePermissions)) {
    return defaultPermissions;
  }

  return parsePermissions(activePermissions);
};